import { createSignal, createEffect, createResource, onMount, untrack, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useApi, isOk } from '../api';
import { useUserContext } from '../components/UserContextProvider';
import { useRoleParser } from '../hooks/permissions';
import Text from '../components/Text';
import PushNotificationToggle from '../components/PushNotificationToggle';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import type { AlertMetric } from '../api/generated/schemas/alertMetric';
import type { AlertComparison } from '../api/generated/schemas/alertComparison';
import type { AlertRuleDto } from '../api/generated/schemas/alertRuleDto';
import type { AlertEventDto } from '../api/generated/schemas/alertEventDto';
import type { AlertMetricCurrentDto } from '../api/generated/schemas/alertMetricCurrentDto';
import type { AlertPreferenceDto } from '../api/generated/schemas/alertPreferenceDto';
import type { WebhookDto } from '../api/generated/schemas/webhookDto';
import styles from './Voting.module.css';
import layout from './AdminAlerts.module.css';
import { firstError } from '../api/envelope';

const POLL_MS = 15_000;

const METRIC_KEYS: Record<number, string> = {
    0: 'ALERTS_METRIC_CONNECTIONS_LOST',
    1: 'ALERTS_METRIC_RECONNECTS',
    2: 'ALERTS_METRIC_POSSESSION_INTERRUPTED',
    3: 'ALERTS_METRIC_VOTE_SITES_DISABLED',
    4: 'ALERTS_METRIC_VOTERS_STALE',
    5: 'ALERTS_METRIC_VOTE_FAILURES',
    6: 'ALERTS_METRIC_ACTIVE_PROXIES',
    7: 'ALERTS_METRIC_PROXY_FAILURES',
    8: 'ALERTS_METRIC_SELF_SHUTDOWNS',
    9: 'ALERTS_METRIC_SELF_SHUTDOWNS_SUPPRESSED',
    10: 'ALERTS_METRIC_NO_FRESH_EXIT',
    11: 'ALERTS_METRIC_VOTE_PROVIDER_OUT_OF_CREDITS',
    12: 'ALERTS_METRIC_GRIEFERGAMES_OUTAGE',
};
const GRIEFERGAMES_OUTAGE_METRIC = 12;
const METRIC_VALUES = Object.keys(METRIC_KEYS).map(Number);

interface RuleForm {
    key: string;
    displayName: string;
    metric: number;
    comparison: number;
    threshold: number;
    windowSeconds: number;
    cooldownSeconds: number;
    webhookId: string;
}

const emptyForm = (): RuleForm => ({
    key: '', displayName: '', metric: 0, comparison: 0,
    threshold: 1, windowSeconds: 60, cooldownSeconds: 900, webhookId: '',
});

export default function AdminAlerts() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { user } = useUserContext();
    const { parseRoles, roles } = useRoleParser();
    const isRoot = () => (parseRoles(user()?.permissions ?? 0, roles))[roles.indexOf('Root')];

    const [rules, setRules] = createStore<AlertRuleDto[]>([]);
    const [events, setEvents] = createStore<AlertEventDto[]>([]);
    const [metrics, setMetrics] = createStore<AlertMetricCurrentDto[]>([]);
    const [pref, setPref] = createSignal<AlertPreferenceDto>({ emailEnabled: false, email: '', pushEnabled: false });
    const [loading, setLoading] = createSignal(true);

    const [emailInput, setEmailInput] = createSignal('');
    const [savingPref, setSavingPref] = createSignal(false);
    const [testing, setTesting] = createSignal(false);

    const [formOpen, setFormOpen] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [form, setForm] = createStore<RuleForm>(emptyForm());
    const [submitting, setSubmitting] = createSignal(false);

    const [webhooks] = createResource(isRoot, async () => {
        const res = await api.webhook.list();
        return isOk(res.data) ? res.data.value!.webhooks : ([] as WebhookDto[]);
    });

    const metricLabel = (m: number) => t(METRIC_KEYS[m] ?? 'ALERTS_METRIC_UNKNOWN');

    const fetchAll = async () => {
        const jobs: Promise<unknown>[] = [
            api.alertsAdmin.currentMetrics(),
            api.alertsAdmin.getPreferences(),
        ];
        if (isRoot()) {
            jobs.push(api.alertsAdmin.listRules(), api.alertsAdmin.listEvents({ take: 50 }));
        }
        const [metricsRes, prefRes, rulesRes, eventsRes] = await Promise.all(jobs) as any[];

        if (isOk(metricsRes.data)) setMetrics(reconcile((metricsRes.data.value.metrics ?? []) as AlertMetricCurrentDto[], { key: 'metric' }));
        if (isOk(prefRes.data)) {
            const p = prefRes.data.value as AlertPreferenceDto;
            setPref(p);
            if (loading()) setEmailInput(p.email ?? '');
        }
        if (rulesRes && isOk(rulesRes.data)) setRules(reconcile((rulesRes.data.value.rules ?? []) as AlertRuleDto[], { key: 'id' }));
        if (eventsRes && isOk(eventsRes.data)) setEvents(reconcile((eventsRes.data.value.events ?? []) as AlertEventDto[], { key: 'id' }));
        if (loading()) setLoading(false);
    };

    const [rootFetchDone, setRootFetchDone] = createSignal(false);

    onMount(() => {
        void fetchAll();
        useVisibilityPoll(() => void fetchAll(), POLL_MS);
    });

    createEffect(() => {
        if (isRoot() && !rootFetchDone()) {
            setRootFetchDone(true);
            untrack(() => void fetchAll());
        }
    });

    const savePref = async (next: AlertPreferenceDto) => {
        setSavingPref(true);
        const res = await api.alertsAdmin.setPreferences(next);
        setSavingPref(false);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ALERTS_PREF_SAVE_FAILED')); return; }
        setPref(res.data.value as AlertPreferenceDto);
        toast?.success(t('ALERTS_PREF_SAVED'));
    };

    const toggleEmail = (enabled: boolean) => void savePref({ ...pref(), emailEnabled: enabled, email: emailInput().trim() });
    const togglePushPref = (enabled: boolean) => void savePref({ ...pref(), pushEnabled: enabled });
    const saveEmailAddress = () => void savePref({ ...pref(), email: emailInput().trim() });

    const sendTest = async () => {
        setTesting(true);
        const res = await api.alertsAdmin.sendTest();
        setTesting(false);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ALERTS_TEST_FAILED')); return; }
        const r = res.data.value as { emailSent: boolean; pushSent: boolean };
        if (!r.emailSent && !r.pushSent) { toast?.info(t('ALERTS_TEST_NO_CHANNEL')); return; }
        toast?.success(t('ALERTS_TEST_SENT'));
    };

    const openCreate = () => { setEditId(null); setForm(emptyForm()); setFormOpen(true); };
    const openEdit = (r: AlertRuleDto) => {
        setEditId(r.id);
        setForm({
            key: r.key, displayName: r.displayName, metric: r.metric, comparison: r.comparison,
            threshold: r.threshold, windowSeconds: r.windowSeconds, cooldownSeconds: r.cooldownSeconds,
            webhookId: r.webhookId ?? '',
        });
        setFormOpen(true);
    };

    const submitForm = async () => {
        if (!editId() && !form.key.trim()) { toast?.error(t('ALERTS_RULE_KEY_REQUIRED')); return; }
        setSubmitting(true);
        const body = {
            displayName: form.displayName.trim() || form.key.trim(),
            metric: form.metric as AlertMetric,
            comparison: form.comparison as AlertComparison,
            threshold: Number(form.threshold),
            windowSeconds: Number(form.windowSeconds),
            cooldownSeconds: Number(form.cooldownSeconds),
            isEnabled: true,
            webhookId: form.webhookId || null,
        };
        const id = editId();
        const res = id
            ? await api.alertsAdmin.updateRule(id, { ...body, isEnabled: rules.find(r => r.id === id)?.isEnabled ?? true })
            : await api.alertsAdmin.createRule({ ...body, key: form.key.trim() });
        setSubmitting(false);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ALERTS_RULE_SAVE_FAILED')); return; }
        toast?.success(id ? t('ALERTS_RULE_UPDATED') : t('ALERTS_RULE_CREATED'));
        setFormOpen(false);
        void fetchAll();
    };

    const toggleRule = async (r: AlertRuleDto, enabled: boolean) => {
        const res = await api.alertsAdmin.updateRule(r.id, {
            displayName: r.displayName, metric: r.metric, comparison: r.comparison,
            threshold: r.threshold, windowSeconds: r.windowSeconds, cooldownSeconds: r.cooldownSeconds,
            isEnabled: enabled, webhookId: r.webhookId ?? null,
        });
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ALERTS_RULE_SAVE_FAILED')); return; }
        toast?.info(enabled ? t('ALERTS_RULE_ENABLED') : t('ALERTS_RULE_DISABLED'));
        void fetchAll();
    };

    const deleteRule = async (r: AlertRuleDto) => {
        const ok = await showConfirm(t('ALERTS_RULE_DELETE_CONFIRM', r.displayName), true);
        if (!ok) return;
        const res = await api.alertsAdmin.deleteRule(r.id);
        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('ALERTS_RULE_DELETE_FAILED')); return; }
        toast?.success(t('ALERTS_RULE_DELETED'));
        void fetchAll();
    };

    const cmpSymbol = (c: number) => (c === 1 ? '≤' : '≥');

    return (
        <div class={layout.container}>
            <div class={styles.headerRow}>
                <h1><Text val="ADMIN_ALERTS_TITLE" /></h1>
                <Show when={isRoot()}>
                    <span class={styles.entryCount}>{rules.length} <Text val="ALERTS_RULES" /></span>
                </Show>
            </div>

            <h2><Text val="ALERTS_LIVE_HEADER" /></h2>
            <Show when={!loading()} fallback={<div class={styles.list}><div class={styles.skeleton} /><div class={styles.skeleton} /></div>}>
                <div class={layout.tileGrid}>
                    <For each={metrics}>{(m) => (
                        <div class="fcard v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>{metricLabel(m.metric)}</span>
                            <span style={{ 'font-size': 'var(--font-size-2xl)', 'font-weight': 600, 'font-family': 'var(--font-family-mono)' }}>{m.value}</span>
                            <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)' }}>{m.context}</span>
                        </div>
                    )}</For>
                </div>
            </Show>

            <div class="h" style={{ 'align-items': 'center', 'justify-content': 'space-between' }}>
                <h2 style={{ margin: 0 }}><Text val="ALERTS_CHANNELS_HEADER" /></h2>
                <button class="btn btn-secondary btn-sm" disabled={testing()} onClick={sendTest}>
                    <Show when={testing()} fallback={<><i class="fa-solid fa-paper-plane" /> <Text val="ALERTS_SEND_TEST" /></>}>
                        <i class="fa-solid fa-spinner fa-spin" /> <Text val="ALERTS_SENDING" />
                    </Show>
                </button>
            </div>
            <div class={layout.channels} style={{ 'margin-top': 'var(--spacing-md)' }}>
                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                    <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-weight': 600 }}><Text val="ALERTS_EMAIL_LABEL" /></span>
                            <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="ALERTS_EMAIL_HINT" /></span>
                        </div>
                        <button class={`btn btn-sm ${pref().emailEnabled ? 'btn-secondary' : 'btn-primary'}`} disabled={savingPref()}
                            onClick={() => toggleEmail(!pref().emailEnabled)}>
                            <Text val={pref().emailEnabled ? 'ALERTS_DISABLE' : 'ALERTS_ENABLE'} />
                        </button>
                    </div>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_EMAIL_ADDRESS" /></span>
                        <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                            <input type="email" style={{ flex: 1 }} placeholder="admin@example.com" value={emailInput()} onInput={(e) => setEmailInput(e.currentTarget.value)} />
                            <button class="btn btn-secondary btn-sm" disabled={savingPref()} onClick={saveEmailAddress}><Text val="SAVE" /></button>
                        </div>
                    </label>
                </div>

                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                    <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-weight': 600 }}><Text val="ALERTS_PUSH_LABEL" /></span>
                            <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}><Text val="ALERTS_PUSH_HINT" /></span>
                        </div>
                        <button class={`btn btn-sm ${pref().pushEnabled ? 'btn-secondary' : 'btn-primary'}`} disabled={savingPref()}
                            onClick={() => togglePushPref(!pref().pushEnabled)}>
                            <Text val={pref().pushEnabled ? 'ALERTS_DISABLE' : 'ALERTS_ENABLE'} />
                        </button>
                    </div>
                    <PushNotificationToggle />
                </div>
            </div>

            <Show when={isRoot()}>
                <div class="h" style={{ 'align-items': 'center', 'justify-content': 'space-between' }}>
                    <h2 style={{ margin: 0 }}><Text val="ALERTS_RULES_HEADER" /></h2>
                    <button class="btn btn-primary btn-sm" onClick={openCreate}>
                        <i class="fa-solid fa-plus" /> <Text val="ALERTS_ADD_RULE" />
                    </button>
                </div>

                <Show when={formOpen()}>
                    <form class="fcard v" style={{ gap: 'var(--spacing-md)', 'margin-top': 'var(--spacing-md)', 'max-width': '38rem' }}
                        onSubmit={(e) => { e.preventDefault(); void submitForm(); }}>
                        <h3 style={{ margin: 0 }}><Text val={editId() ? 'ALERTS_EDIT_RULE' : 'ALERTS_ADD_RULE'} /></h3>
                        <Show when={!editId()}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_FIELD_KEY" /></span>
                                <input type="text" placeholder="reconnect-spike" value={form.key} onInput={(e) => setForm('key', e.currentTarget.value)} />
                            </label>
                        </Show>
                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_FIELD_NAME" /></span>
                            <input type="text" value={form.displayName} onInput={(e) => setForm('displayName', e.currentTarget.value)} />
                        </label>
                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_FIELD_METRIC" /></span>
                            <select value={form.metric} onChange={(e) => setForm('metric', Number(e.currentTarget.value))}>
                                <For each={METRIC_VALUES}>{(m) => <option value={m}>{metricLabel(m)}</option>}</For>
                            </select>
                            <Show when={form.metric === GRIEFERGAMES_OUTAGE_METRIC}>
                                <span style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_METRIC_GRIEFERGAMES_OUTAGE_HINT" /></span>
                            </Show>
                        </label>
                        <div class="h" style={{ gap: 'var(--spacing-md)' }}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_FIELD_COMPARISON" /></span>
                                <select value={form.comparison} onChange={(e) => setForm('comparison', Number(e.currentTarget.value))}>
                                    <option value={0}><Text val="ALERTS_CMP_GTE" /></option>
                                    <option value={1}><Text val="ALERTS_CMP_LTE" /></option>
                                </select>
                            </label>
                            <label class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_FIELD_THRESHOLD" /></span>
                                <input type="number" step="any" value={form.threshold} onInput={(e) => setForm('threshold', Number(e.currentTarget.value))} />
                            </label>
                        </div>
                        <div class="h" style={{ gap: 'var(--spacing-md)' }}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_FIELD_WINDOW" /></span>
                                <input type="number" min="1" value={form.windowSeconds} onInput={(e) => setForm('windowSeconds', Number(e.currentTarget.value))} />
                            </label>
                            <label class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_FIELD_COOLDOWN" /></span>
                                <input type="number" min="0" value={form.cooldownSeconds} onInput={(e) => setForm('cooldownSeconds', Number(e.currentTarget.value))} />
                            </label>
                        </div>
                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="ALERTS_FIELD_WEBHOOK" /></span>
                            <select value={form.webhookId} disabled={webhooks.loading} onChange={(e) => setForm('webhookId', e.currentTarget.value)}>
                                <option value=""><Text val="ALERTS_WEBHOOK_NONE" /></option>
                                <For each={webhooks() ?? []}>{(w) => <option value={w.id}>{w.name}</option>}</For>
                            </select>
                        </label>
                        <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                            <button type="button" class="btn btn-secondary" onClick={() => setFormOpen(false)}><Text val="CANCEL" /></button>
                            <button type="submit" class="btn btn-primary" disabled={submitting()}>
                                <Show when={submitting()} fallback={<Text val="SAVE" />}><i class="fa-solid fa-spinner fa-spin" /></Show>
                            </button>
                        </div>
                    </form>
                </Show>

                <Show when={!loading()} fallback={<div class={styles.list}><div class={styles.skeleton} /></div>}>
                    <Show when={rules.length > 0} fallback={
                        <div class="fcard v" style={{ 'align-items': 'center', gap: 'var(--spacing-sm)', padding: 'var(--spacing-2xl)' }}>
                            <i class="fa-solid fa-bell-slash" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                            <p style={{ color: 'var(--dark-text-muted)' }}><Text val="ALERTS_RULES_EMPTY" /></p>
                        </div>
                    }>
                        <div class="fcard" style={{ 'margin-top': 'var(--spacing-md)' }}>
                            <div class={styles.tableWrap}>
                                <table class={styles.logTable}>
                                    <thead>
                                        <tr>
                                            <th><Text val="ALERTS_COL_NAME" /></th>
                                            <th><Text val="ALERTS_COL_CONDITION" /></th>
                                            <th><Text val="ALERTS_COL_WINDOW" /></th>
                                            <th><Text val="ALERTS_COL_COOLDOWN" /></th>
                                            <th><Text val="ALERTS_COL_WEBHOOK" /></th>
                                            <th><Text val="ALERTS_COL_STATE" /></th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <For each={rules}>{(r) => (
                                            <tr>
                                                <td>{r.displayName}<div style={{ 'font-family': 'var(--font-family-mono)', 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)' }}>{r.key}</div></td>
                                                <td style={{ 'font-family': 'var(--font-family-mono)' }}>{metricLabel(r.metric)} {cmpSymbol(r.comparison)} {r.threshold}</td>
                                                <td>{r.windowSeconds}s</td>
                                                <td>{r.cooldownSeconds}s</td>
                                                <td>{r.webhookName ?? <span style={{ color: 'var(--dark-text-muted)' }}>-</span>}</td>
                                                <td>
                                                    <span class={`${styles.outcomeBadge} ${r.isEnabled ? styles.outcomeOk : styles.outcomeBad}`}>
                                                        <Text val={r.isEnabled ? 'ALERTS_STATE_ON' : 'ALERTS_STATE_OFF'} />
                                                    </span>
                                                </td>
                                                <td>
                                                    <div class="h" style={{ gap: 'var(--spacing-xs)' }}>
                                                        <button class="btn btn-secondary btn-sm" title={t(r.isEnabled ? 'ALERTS_RULE_DISABLE' : 'ALERTS_RULE_ENABLE')}
                                                            onClick={() => void toggleRule(r, !r.isEnabled)}>
                                                            <i class={`fa-solid ${r.isEnabled ? 'fa-pause' : 'fa-play'}`} />
                                                        </button>
                                                        <button class="btn btn-secondary btn-sm" title={t('EDIT')} onClick={() => openEdit(r)}>
                                                            <i class="fa-solid fa-pen-to-square" />
                                                        </button>
                                                        <button class="btn btn-danger btn-sm" title={t('DELETE')} onClick={() => void deleteRule(r)}>
                                                            <i class="fa-solid fa-trash" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}</For>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Show>
                </Show>

                <h2 style={{ 'margin-top': 'var(--spacing-2xl)' }}><Text val="ALERTS_RECENT_HEADER" /></h2>
                <Show when={!loading()} fallback={<div class={styles.list}><div class={styles.skeleton} /></div>}>
                    <Show when={events.length > 0} fallback={
                        <div class="fcard v" style={{ 'align-items': 'center', gap: 'var(--spacing-sm)', padding: 'var(--spacing-2xl)' }}>
                            <i class="fa-solid fa-inbox" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                            <p style={{ color: 'var(--dark-text-muted)' }}><Text val="ALERTS_RECENT_EMPTY" /></p>
                        </div>
                    }>
                        <div class="fcard">
                            <div class={styles.tableWrap}>
                                <table class={styles.logTable}>
                                    <thead>
                                        <tr>
                                            <th><Text val="ALERTS_COL_WHEN" /></th>
                                            <th><Text val="ALERTS_COL_NAME" /></th>
                                            <th><Text val="ALERTS_COL_VALUE" /></th>
                                            <th><Text val="ALERTS_COL_MESSAGE" /></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <For each={events}>{(ev) => (
                                            <tr>
                                                <td>{formatDateTime(ev.firedAtUtc)}</td>
                                                <td>{ev.ruleDisplayName}</td>
                                                <td style={{ 'font-family': 'var(--font-family-mono)' }}>{ev.observedValue} / {ev.threshold}</td>
                                                <td style={{ color: 'var(--dark-text-muted)' }}>{ev.message}</td>
                                            </tr>
                                        )}</For>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Show>
                </Show>
            </Show>
        </div>
    );
}
